import { useCallback, useEffect, useRef, useState } from 'react';

import { useReadyKakaopayRegistration } from '@/entities/card/hooks';
import usePollingUserPayments from '@/hooks/common/usePollingUserPayments';
import useUserPointAmount from '@/hooks/common/useUserPointAmount';
import { CardPayCompany, UserAddress, UserPaymentsInfo } from '@/models/common';
import { TreatmentRequest } from '@/models/diagnosis';
import { Client, ClientType } from '@/types/app';
import { useToast } from 'doctornow-design-system';
import { CardCompanyButtonType } from './useTelemedicineRequestViewModel';

interface UseTelemedicinePaymentParams {
  treatmentRequest: TreatmentRequest;
  updateForm: (updater: (prev: any) => any) => void;
  client: Client;
}

export const useTelemedicinePayment = ({ treatmentRequest, updateForm, client }: UseTelemedicinePaymentParams) => {
  const { toast } = useToast();
  const { point } = useUserPointAmount(treatmentRequest.shouldShowPointUseRequest());
  const {
    userPaymentsInfo,
    refetchUserPayments,
    isUserPaymentsInfoRefetching,
    isUserPaymentsInfoFetched,
    startPollingUserPayments,
    isPollingUserPayments,
  } = usePollingUserPayments({ enabled: false, enablePolling: true });
  const { onSubmitReadyKakaopayRegistration } = useReadyKakaopayRegistration();
  const [cardCompanyButton, setCardCompanyButton] = useState<CardCompanyButtonType>();

  const treatmentRequestRef = useRef(treatmentRequest);
  const updateFormRef = useRef(updateForm);
  const previousPollingRef = useRef(false);
  const previousPaymentCountRef = useRef(0);
  const isInitializedRef = useRef(false);

  useEffect(() => {
    treatmentRequestRef.current = treatmentRequest;
  }, [treatmentRequest]);

  useEffect(() => {
    updateFormRef.current = updateForm;
  }, [updateForm]);

  useEffect(() => {
    if (!isInitializedRef.current) {
      previousPaymentCountRef.current = userPaymentsInfo.payments.length;
      isInitializedRef.current = true;
    }
  }, [userPaymentsInfo.payments.length]);

  useEffect(() => {
    const wasPolling = previousPollingRef.current;
    const isNowNotPolling = !isPollingUserPayments;
    const currentPaymentCount = userPaymentsInfo.payments.length;
    const hasNewPayment = isInitializedRef.current && currentPaymentCount > previousPaymentCountRef.current;

    if (wasPolling && isNowNotPolling && hasNewPayment) {
      const firstPayment = userPaymentsInfo.payments[0];

      if (firstPayment) {
        const currentRequest = treatmentRequestRef.current;
        const form = currentRequest.selectCard(firstPayment.id);
        updateFormRef.current((prev) => ({ ...prev, ...form }));

        if (firstPayment.payCompany === CardPayCompany.Kakaopay) {
          setCardCompanyButton(CardCompanyButtonType.KAKAOPAY);
        } else {
          setCardCompanyButton(CardCompanyButtonType.ANOTHER);
        }

        previousPaymentCountRef.current = currentPaymentCount;
      }
    }

    previousPollingRef.current = isPollingUserPayments;
  }, [isPollingUserPayments, userPaymentsInfo]);

  const selectCard = useCallback((paymentId: string) => {
    const currentRequest = treatmentRequestRef.current;
    const form = currentRequest.selectCard(paymentId);
    updateFormRef.current((prev) => ({ ...prev, ...form }));
  }, []);

  const updateInstallment = useCallback((installmentMonth: number) => {
    const currentRequest = treatmentRequestRef.current;
    const form = currentRequest.updateInstallment(installmentMonth);
    updateFormRef.current(() => form);
  }, []);

  const toggleRequestUsePoint = useCallback(() => {
    if (point < 100) {
      toast({
        text: '100P 이상부터 사용할 수 있어요.',
        direction: 'bottom',
      });
      return;
    }

    const currentRequest = treatmentRequestRef.current;
    const form = currentRequest.toggleRequestUsePoint();
    updateFormRef.current(() => form);
  }, [point, toast]);

  const selectCardCompanyButton = useCallback((company: CardCompanyButtonType) => {
    setCardCompanyButton(company);
  }, []);

  const selectAddress = useCallback((selectedAddress: UserAddress) => {
    const currentRequest = treatmentRequestRef.current;
    updateFormRef.current((prevForm) => currentRequest.selectAddress(prevForm, selectedAddress.userAddressId ?? ''));
  }, []);

  const addKakaopay = useCallback(async () => {
    try {
      const result = await onSubmitReadyKakaopayRegistration();
      const { nextRedirectMobileUrl, nextRedirectPcUrl } = result;
      if (client.clientType === ClientType.WEB) {
        window.open(nextRedirectPcUrl);
        startPollingUserPayments();
        return;
      }
      if (client.clientType === ClientType.MOBILE_WEB) {
        window.open(nextRedirectMobileUrl);
        startPollingUserPayments();
        return;
      }
    } catch (error) {
      if (error instanceof Error) {
        toast({
          text: error instanceof Error ? error.message : '카카오페이 등록 정보를 가져오는데 실패했습니다.',
          direction: 'bottom',
        });
      }
    }
  }, [client.clientType, onSubmitReadyKakaopayRegistration, startPollingUserPayments, toast]);

  const selectInitCreditCard = useCallback(
    (paymentsInfo: UserPaymentsInfo) => {
      const currentRequest = treatmentRequestRef.current;
      const defaultPayment = paymentsInfo.findDefaultPayment();

      if (defaultPayment) {
        const form = currentRequest.selectCard(defaultPayment.id);
        updateFormRef.current((prev) => ({ ...prev, ...form }));

        if (defaultPayment.payCompany === CardPayCompany.Kakaopay) {
          selectCardCompanyButton(CardCompanyButtonType.KAKAOPAY);
        } else {
          selectCardCompanyButton(CardCompanyButtonType.ANOTHER);
        }
      } else {
        selectCardCompanyButton(CardCompanyButtonType.ANOTHER);
      }
    },
    [selectCardCompanyButton]
  );

  return {
    point,
    cardCompanyButton,
    userPaymentsInfo,
    isUserPaymentsInfoRefetching,
    isUserPaymentsInfoFetched,
    selectCard,
    updateInstallment,
    toggleRequestUsePoint,
    selectCardCompanyButton,
    selectAddress,
    addKakaopay,
    refetchUserPayments,
    selectInitCreditCard,
  };
};
